import { z } from 'zod';
import { CREATED_RANGE_SHAPE, PageQuerySchema, UuidSchema } from './common';
import { EntityIntegrationSchema } from './integration';
import {
  IncidentCountsSchema,
  ProjectMetricsSchema,
  RatingsSchema,
  SurveyCountsSchema,
} from './metrics';
import {
  IncidentStatusSchema,
  QuestionTypeSchema,
  RunStatusSchema,
  ScheduleStatusSchema,
  TemplateStatusSchema,
} from './enums';

/** §9.1, §9.2, §9.4 ТЗ. Анкеты, расписания, запуски, инциденты. */

// ───────────────────────────── Анкеты и версии ─────────────────────────────

/**
 * Вопрос в составе версии. Ключ стабилен между версиями (§9.1) —
 * по нему аналитика сравнивает динамику через смену редакций.
 */
export const QuestionInputSchema = z
  .object({
    key: z
      .string()
      .regex(/^[a-z][a-z0-9_]{1,49}$/, 'Ключ: латиница, цифры и подчёркивание, до 50 символов'),
    text: z.string().trim().min(3).max(500),
    type: QuestionTypeSchema,
    required: z.boolean().default(true),
    /** участвует в правиле негатива; имеет смысл только для RATING_1_5 (§9.4) */
    scored: z.boolean().default(false),
    /** варианты для SINGLE_CHOICE, 2–10 штук */
    options: z.array(z.string().trim().min(1).max(200)).min(2).max(10).optional(),
  })
  .superRefine((q, issues) => {
    if (q.type === 'SINGLE_CHOICE' && !q.options) {
      issues.addIssue({ code: z.ZodIssueCode.custom, message: 'SINGLE_CHOICE требует options' });
    }
    if (q.type !== 'SINGLE_CHOICE' && q.options) {
      issues.addIssue({ code: z.ZodIssueCode.custom, message: 'options только у SINGLE_CHOICE' });
    }
    if (q.type !== 'RATING_1_5' && q.scored) {
      issues.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'В правиле негатива участвуют только оценочные вопросы (§9.4)',
      });
    }
  });
export type QuestionInput = z.infer<typeof QuestionInputSchema>;

/** §20: до 10 вопросов. Ключи в пределах версии уникальны. */
export const QuestionListSchema = z
  .array(QuestionInputSchema)
  .min(1)
  .max(10)
  .superRefine((list, issues) => {
    const keys = new Set(list.map((q) => q.key));
    if (keys.size !== list.length) {
      issues.addIssue({ code: z.ZodIssueCode.custom, message: 'Ключи вопросов повторяются' });
    }
  });

export const QuestionSchema = z.object({
  key: z.string(),
  text: z.string(),
  type: QuestionTypeSchema,
  order: z.number().int().min(1),
  required: z.boolean(),
  scored: z.boolean(),
  options: z.array(z.string()).nullable(),
});
export type Question = z.infer<typeof QuestionSchema>;

export const TemplateVersionSummarySchema = z.object({
  id: UuidSchema,
  version: z.number().int().min(1),
  status: TemplateStatusSchema,
  publishedAt: z.string().datetime().nullable(),
  /** черновик правится, опубликованная версия — нет; в таблице это разные колонки */
  updatedAt: z.string().datetime(),
  questionCount: z.number().int().min(0),
});
export type TemplateVersionSummary = z.infer<typeof TemplateVersionSummarySchema>;

export const TemplateVersionDetailSchema = TemplateVersionSummarySchema.extend({
  templateId: UuidSchema,
  templateName: z.string(),
  negativeRule: z.unknown(),
  questions: z.array(QuestionSchema),
});
export type TemplateVersionDetail = z.infer<typeof TemplateVersionDetailSchema>;

export const TemplateSummarySchema = z.object({
  id: UuidSchema,
  name: z.string(),
  archivedAt: z.string().datetime().nullable(),
  versions: z.array(TemplateVersionSummarySchema),
});
export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;

export const CreateTemplateRequestSchema = z.object({
  name: z.string().trim().min(3).max(200),
  questions: QuestionListSchema,
});
export type CreateTemplateRequest = z.infer<typeof CreateTemplateRequestSchema>;

/** Правится только DRAFT-версия; правка опубликованной идёт через новый черновик (§9.1). */
export const UpdateDraftRequestSchema = z.object({
  name: z.string().trim().min(3).max(200).optional(),
  questions: QuestionListSchema,
});
export type UpdateDraftRequest = z.infer<typeof UpdateDraftRequestSchema>;

// ───────────────────────────── Расписания ─────────────────────────────

export const PeriodGranularitySchema = z.enum(['DAY', 'WEEK', 'MONTH']);
export type PeriodGranularityValue = z.infer<typeof PeriodGranularitySchema>;

export const CreateScheduleRequestSchema = z
  .object({
    name: z.string().trim().min(3).max(200),
    /** ровно одно из двух: объект или тег группы (§9.2) */
    projectId: UuidSchema.optional(),
    projectGroupTag: z.string().trim().min(1).max(100).optional(),
    versionId: UuidSchema,
    /** поддерживаемое подмножество RRULE — см. spec/40-surveys.md */
    calendarRule: z.string().trim().min(5).max(200),
    localTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Ожидается HH:mm'),
    periodGranularity: PeriodGranularitySchema,
    /** D4: 52 часа на ответ — ответ заказчика 22.10.2026 */
    responseWindowH: z.number().int().min(1).max(720).default(52),
    /** §20: не более двух напоминаний; D5 — интервал 24 часа */
    reminderOffsetsH: z.array(z.number().int().min(1).max(720)).max(2).default([24, 48]),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime().optional(),
  })
  .superRefine((s, issues) => {
    if (!s.projectId === !s.projectGroupTag) {
      issues.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Укажите либо объект, либо тег группы — ровно одно (§9.2)',
      });
    }
    const sorted = [...s.reminderOffsetsH].sort((a, b) => a - b);
    if (JSON.stringify(sorted) !== JSON.stringify(s.reminderOffsetsH)) {
      issues.addIssue({ code: z.ZodIssueCode.custom, message: 'Смещения — по возрастанию' });
    }
    if (s.reminderOffsetsH.some((h) => h >= s.responseWindowH)) {
      issues.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Напоминание после дедлайна бессмысленно',
      });
    }
  });
export type CreateScheduleRequest = z.infer<typeof CreateScheduleRequestSchema>;

export const UpdateScheduleRequestSchema = z
  .object({
    name: z.string().trim().min(3).max(200).optional(),
    calendarRule: z.string().trim().min(5).max(200).optional(),
    localTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .optional(),
    periodGranularity: PeriodGranularitySchema.optional(),
    responseWindowH: z.number().int().min(1).max(720).optional(),
    reminderOffsetsH: z.array(z.number().int().min(1).max(720)).max(2).optional(),
    endsAt: z.string().datetime().nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'Не передано ни одного поля для изменения',
  });
export type UpdateScheduleRequest = z.infer<typeof UpdateScheduleRequestSchema>;

export const ScheduleStatusRequestSchema = z.object({
  status: ScheduleStatusSchema,
});
export type ScheduleStatusRequest = z.infer<typeof ScheduleStatusRequestSchema>;

export const ScheduleSummarySchema = z.object({
  id: UuidSchema,
  name: z.string(),
  projectId: UuidSchema.nullable(),
  projectName: z.string().nullable(),
  projectGroupTag: z.string().nullable(),
  versionId: UuidSchema,
  templateName: z.string(),
  version: z.number().int(),
  calendarRule: z.string(),
  localTime: z.string(),
  periodGranularity: z.string(),
  responseWindowH: z.number().int(),
  reminderOffsetsH: z.array(z.number().int()),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().nullable(),
  status: ScheduleStatusSchema,
  lastRunAt: z.string().datetime().nullable(),
  /**
   * Ближайший запуск по правилу (§9.2). Считает сервер тем же кодом, что
   * планировщик: посчитать в браузере значило бы завести вторую реализацию
   * RRULE, которая разойдётся с первой. null — расписание не активно или
   * закончилось.
   */
  nextRunAt: z.string().datetime().nullable(),
  /**
   * §8: зона, в которой посчитан `nextRunAt`. У расписания на объект это зона
   * объекта, у группового — зона того объекта группы, который дал самый ранний
   * момент. Без неё экран разворачивал групповой запуск в зоне по умолчанию,
   * и в одной строке стояли «пн 10:00» и «След.: 03:00» (QA 22.08.2026).
   * `null` — расписание не стреляет, разворачивать нечего.
   */
  nextRunTimezone: z.string().nullable(),
});
export type ScheduleSummary = z.infer<typeof ScheduleSummarySchema>;

export const ListSchedulesQuerySchema = PageQuerySchema.extend({
  projectId: UuidSchema.optional(),
  status: ScheduleStatusSchema.optional(),
});
export type ListSchedulesQuery = z.infer<typeof ListSchedulesQuerySchema>;

// ───────────────────────────── Запуски ─────────────────────────────

export const RunSummarySchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  projectName: z.string(),
  personName: z.string(),
  periodKey: z.string(),
  version: z.number().int(),
  status: RunStatusSchema,
  channel: z.string(),
  dueAt: z.string().datetime(),
  sentAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  remindersSent: z.number().int(),
  lateAnswer: z.boolean(),
  hasIncident: z.boolean(),
});
export type RunSummary = z.infer<typeof RunSummarySchema>;

/**
 * §13 ТЗ. Отбор запусков: один и тот же для таблицы, выгрузки и сводки.
 *
 * Объявлен формой, а не тремя похожими схемами: сводка над таблицей обязана
 * считаться по тем же строкам, которые в таблице видно. Разъехавшиеся отборы
 * дали бы «завершено 12» над списком из восьми записей.
 */
export const RUNS_FILTER_SHAPE = {
  ...CREATED_RANGE_SHAPE,
  projectId: UuidSchema.optional(),
  status: RunStatusSchema.optional(),
  periodKey: z.string().optional(),
  /** наличие негатива (§14). Строка из query, не coerce: Boolean('false') === true */
  hasIncident: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
} as const;

export const RunsFilterSchema = z.object(RUNS_FILTER_SHAPE);
export type RunsFilter = z.infer<typeof RunsFilterSchema>;

export const ListRunsQuerySchema = PageQuerySchema.extend(RUNS_FILTER_SHAPE);
export type ListRunsQuery = z.infer<typeof ListRunsQuerySchema>;

/**
 * §13, §14 ТЗ. Сводка раздела «Результаты опросов».
 *
 * Те же показатели, что на дашборде, но по отбору таблицы и без периода
 * по умолчанию: дашборд без дат показывает три месяца, таблица — всё, и
 * подставлять здесь умолчание значило бы врать подписью к цифрам.
 */
export const RunsSummarySchema = z.object({
  generatedAt: z.string().datetime(),
  runs: SurveyCountsSchema,
  /**
   * Те же счётчики, но посчитанные без фильтра статуса. Плитки-переключатели
   * показывают именно их: считая счётчик «Просрочено» по отбору, в который уже
   * входит «статус = завершён», получаешь ноль — и переключатель выглядит
   * сломанным вместо того, чтобы предлагать переход.
   */
  statusFacets: SurveyCountsSchema,
  /**
   * Инциденты по отбору без фильтра «наличие негатива» — по той же причине,
   * что statusFacets: плитка-переключатель обязана предлагать переход, а не
   * показывать ноль, потому что текущий отбор инциденты как раз исключает.
   */
  incidentFacets: IncidentCountsSchema,
  /**
   * Периоды, встречающиеся в отборе без фильтра периода: из них строится
   * выпадающий список. Без этого фасета список после выбора периода
   * схлопывался бы до одного пункта, из которого некуда вернуться.
   */
  periodFacets: z.array(z.string()),
  ratings: RatingsSchema,
  incidents: IncidentCountsSchema,
  /** разрез по объектам: диаграмма «по объекту», а на одном объекте — одна строка */
  byProject: z.array(ProjectMetricsSchema),
});
export type RunsSummary = z.infer<typeof RunsSummarySchema>;

/** Ответ в карточке запуска: значение вместе с вопросом версии (§9.3). */
export const RunAnswerSchema = z.object({
  questionKey: z.string(),
  questionText: z.string(),
  questionType: QuestionTypeSchema,
  scored: z.boolean(),
  valueNumber: z.number().int().nullable(),
  valueText: z.string().nullable(),
  valueOption: z.string().nullable(),
  answeredAt: z.string().datetime(),
  source: z.string(),
});
export type RunAnswer = z.infer<typeof RunAnswerSchema>;

export const RunDetailSchema = RunSummarySchema.extend({
  scheduleName: z.string().nullable(),
  templateName: z.string(),
  /** время в карточке показывается в часовом поясе объекта (§19) */
  projectTimezone: z.string(),
  incidentId: UuidSchema.nullable(),
  incidentNumber: z.string().nullable(),
  answers: z.array(RunAnswerSchema),
});
export type RunDetail = z.infer<typeof RunDetailSchema>;

// ───────────────────────────── Инциденты ─────────────────────────────

export const IncidentSummarySchema = z.object({
  id: UuidSchema,
  number: z.string(),
  projectId: UuidSchema,
  projectName: z.string(),
  personName: z.string(),
  periodKey: z.string(),
  status: IncidentStatusSchema,
  managerSnapshotName: z.string().nullable(),
  bitrixTaskId: z.number().int().nullable(),
  createdAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable(),
  /**
   * Единый срок реакции (§13, настройка `task_reaction_hours`), отсчитанный
   * от создания. Считается по действующей настройке, снимка у инцидента нет:
   * срок один на сервис, и меняют его для того, чтобы новый применился к работе.
   */
  reactionDueAt: z.string().datetime(),
  /** срок вышел, а инцидент ещё не закрыт и не отклонён */
  overdue: z.boolean(),
});
export type IncidentSummary = z.infer<typeof IncidentSummarySchema>;

export const IncidentDetailSchema = IncidentSummarySchema.extend({
  runId: UuidSchema,
  /** сработавшие вопросы, значения, порог, комментарий (§9.4) */
  ruleSnapshot: z.unknown(),
  externalKey: z.string(),
  /** время в карточке показывается в часовом поясе объекта (§19) */
  projectTimezone: z.string(),
  /** блок «Интеграция»: состояние команды CREATE_TASK (§13, сценарий 34) */
  integration: EntityIntegrationSchema.nullable(),
});
export type IncidentDetail = z.infer<typeof IncidentDetailSchema>;

export const INCIDENTS_FILTER_SHAPE = {
  ...CREATED_RANGE_SHAPE,
  projectId: UuidSchema.optional(),
  status: IncidentStatusSchema.optional(),
  /** §14 «из показателя — к записи»: точный номер INC-… из карточки запуска */
  number: z.string().trim().min(1).max(32).optional(),
  /** только открытые с истёкшим сроком реакции */
  overdueOnly: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
} as const;

export const ListIncidentsQuerySchema = PageQuerySchema.extend(INCIDENTS_FILTER_SHAPE);
export type ListIncidentsQuery = z.infer<typeof ListIncidentsQuerySchema>;

export const IncidentsFilterSchema = z.object(INCIDENTS_FILTER_SHAPE);
export type IncidentsFilter = z.infer<typeof IncidentsFilterSchema>;

/**
 * §13 ТЗ. Сводка раздела «Инциденты»: та же связка «показатель → отбор», что
 * в «Результатах». Считается по тому же отбору, что таблица, но без фильтра
 * статуса — иначе плитка выбранного статуса была бы единственной ненулевой.
 */
export const IncidentsSummarySchema = z.object({
  generatedAt: z.string().datetime(),
  counts: IncidentCountsSchema,
  /** открытые, у которых вышел срок реакции (§13) */
  overdue: z.number().int().min(0),
});
export type IncidentsSummary = z.infer<typeof IncidentsSummarySchema>;

/** E3: переходы статусной модели инцидента. */
export const IncidentStatusRequestSchema = z.object({
  status: z.enum(['IN_PROGRESS', 'CLOSED', 'REJECTED']),
});
export type IncidentStatusRequest = z.infer<typeof IncidentStatusRequestSchema>;
